import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Sparkles,
  FileText, CheckCircle, FileEdit, BarChart3,
} from "lucide-react";
import { AdminHeader } from "@/components/AdminHeader";
import { EmailStatsPanel } from "@/components/EmailStatsPanel";
import { SuspectEventsPanel } from "@/components/SuspectEventsPanel";
import api from "@/lib/api";
import { catBySlug } from "@/data/categories";
import { FactFormModal } from "@/components/FactFormModal";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-xs uppercase tracking-widest text-ink-muted">{label}</span>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <p className="font-display text-3xl font-extrabold" style={{ color: accent }}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [facts, setFacts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/facts").then((r) => setFacts(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (f) => { setEditing(f); setModalOpen(true); };

  const togglePublish = async (f) => {
    await api.put(`/admin/facts/${f.id}`, { ...f, published: !f.published });
    toast.success(f.published ? "Moved to drafts" : "Published", { className: "font-mono" });
    load();
  };

  const doDelete = async (f) => {
    await api.delete(`/admin/facts/${f.id}`);
    toast.success("Fact deleted", { className: "font-mono" });
    setConfirmDelete(null);
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink">Facts Dashboard</h1>
          <button data-testid="admin-create-fact-button" onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-mono text-sm hover:bg-gold hover:text-ink transition-colors">
            <Plus className="w-4 h-4" /> New Fact
          </button>
        </div>

        <SuspectEventsPanel />
        <EmailStatsPanel />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard icon={FileText} label="Total" value={stats?.total ?? "—"} accent="#0F172A" />
          <StatCard icon={CheckCircle} label="Published" value={stats?.published ?? "—"} accent="#2563EB" />
          <StatCard icon={FileEdit} label="Drafts" value={stats?.drafts ?? "—"} accent="#64748B" />
          <StatCard icon={Sparkles} label="AI Generated" value={stats?.ai_generated ?? "—"} accent="#7C3AED" />
          <StatCard icon={BarChart3} label="Total Views" value={stats?.total_views?.toLocaleString?.() ?? "—"} accent="#0EA5E9" />
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left font-mono text-xs uppercase tracking-wider text-ink-muted bg-slate-50">
                  <th className="px-5 py-4">Title</th>
                  <th className="px-5 py-4 hidden md:table-cell">Category</th>
                  <th className="px-5 py-4 hidden lg:table-cell">Status</th>
                  <th className="px-5 py-4 hidden lg:table-cell">Views</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {facts.map((f) => (
                  <tr key={f.id} data-testid={`admin-fact-row-${f.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-4 max-w-md">
                      <div className="flex items-center gap-2">
                        {f.ai_generated && <Sparkles className="w-3.5 h-3.5 text-gold shrink-0" />}
                        <span className="font-medium text-ink line-clamp-1">{f.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-mono border ${catBySlug(f.category).chip}`}>{catBySlug(f.category).name}</span>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className={`font-mono text-xs ${f.published ? "text-gold" : "text-amber-600"}`}>{f.published ? "Published" : "Draft"}</span>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell font-mono text-xs text-ink-muted">{f.views?.toLocaleString?.()}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`admin-toggle-publish-${f.id}`} onClick={() => togglePublish(f)} title={f.published ? "Unpublish" : "Publish"} className="p-2 rounded-lg text-ink-muted hover:text-gold hover:bg-slate-100">
                          {f.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button data-testid={`admin-edit-${f.id}`} onClick={() => openEdit(f)} className="p-2 rounded-lg text-ink-muted hover:text-gold hover:bg-slate-100">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`admin-delete-${f.id}`} onClick={() => setConfirmDelete(f)} className="p-2 rounded-lg text-ink-muted hover:text-red-600 hover:bg-slate-100">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {facts.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-16 text-center font-mono text-sm text-ink-muted">No facts yet. Create your first oddity.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <FactFormModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} editing={editing} />

      {confirmDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white shadow-xl p-6">
            <h3 className="font-display text-xl font-bold mb-2 text-ink">Delete this fact?</h3>
            <p className="text-sm text-ink-muted mb-6 line-clamp-2">"{confirmDelete.title}"</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-xl border border-slate-300 text-ink-muted font-mono text-sm">Cancel</button>
              <button data-testid="confirm-delete-button" onClick={() => doDelete(confirmDelete)} className="px-4 py-2 rounded-xl bg-red-600 text-white font-mono text-sm hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
